import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, from, map, Observable } from 'rxjs';
import { CONFIG_URLS_API } from 'src/environments/config-envs';
import { environment } from 'src/environments/environment';
import {
  IBaseResponse,
  IPostResponsePrecosList,
  IPrecoPosto,
  IResponsePrecosList,
} from './central.interface';

@Injectable({
  providedIn: 'root',
})
export class CentralService {
  private readonly http = inject(HttpClient);
  private qtdDiasCombustivelAtrasado = 0;

  private readonly httpOptions = {
    headers: new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
    }),
  };

  private getDescontoEmReais(
    precoReal: number,
    precoComdesconto: number,
  ): number {
    let desconto =
      precoComdesconto > 0
        ? parseFloat((precoReal - precoComdesconto).toFixed(2))
        : 0;
    return desconto;
  }

  private getPrecoCombustiveisDoPosto(
    codPosto: number,
    guid: string,
  ): Observable<IResponsePrecosList> {
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
    };

    const payload = JSON.stringify({ codPosto: codPosto, guid: guid });

    return this.http.post<IResponsePrecosList>(
      `${environment.host_ctf_service}${CONFIG_URLS_API.API_ConsultaDePreco}`,
      JSON.stringify(payload),
      httpOptions,
    );
  }

  private precoDesatualizado(data: Date): boolean {
    const diffDays = Math.ceil(
      (Date.now() - new Date(data).getTime()) / (1000 * 3600 * 24),
    );
    return this.qtdDiasCombustivelAtrasado > 0
      ? diffDays > this.qtdDiasCombustivelAtrasado
      : false;
  }

  // async verificarRedirecionamentoTelaAntiga(guid: string): Promise<boolean> {
  //   var httpOptions = {
  //     headers: new HttpHeaders({
  //       Accept: 'application/json',
  //       'Content-Type': 'application/json',
  //       UserGuid: guid,
  //     }),
  //   };

  //   const payload = new Promise<boolean>((resolve, reject) => {
  //     this.http
  //       .get<IBaseResponse>(
  //         `${environment.host_ctf_service}${CONFIG_URLS_API.API_ConsultaAcessoTelaPrecosNova}`,
  //         httpOptions,
  //       )
  //       .subscribe(
  //         (result) => {
  //           console.log(result);
  //           resolve(result.retorno);
  //         },

  //         (error) => {
  //           console.error('Erro ao verificar redirecionamento:', error);
  //           resolve(false); // Always resolves with false on error
  //         },
  //       );
  //   });

  //   return payload;
  // }

  private buscarQtdDiasReferenciaParaAtrazos(guid: string): Promise<number> {
    var httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
        UserGuid: guid,
      }),
    };
    return new Promise((resolve, reject) => {
      const url = `${environment.host_ctf_service}${CONFIG_URLS_API.API_Pegar_Qtddias_CombustivelAtrasado}`;
      resolve(
        firstValueFrom(
          this.http.get<IBaseResponse>(url, httpOptions).pipe(
            map((response: IBaseResponse) => {
              return response.retorno;
            }),
          ),
        ).catch((err) => {
          console.error('Erro ao buscar quantidade de dias:', err.message);
          return 0;
        }),
      );
    });
  }

  private async getPrecos(
    codPosto: number,
    guid: string,
  ): Promise<IPrecoPosto[]> {
    try {
      const qtdDias = await this.buscarQtdDiasReferenciaParaAtrazos(guid);
      this.qtdDiasCombustivelAtrasado = qtdDias;

      const response = await firstValueFrom(
        this.getPrecoCombustiveisDoPosto(codPosto, guid),
      );

      if (response?.sucesso) {
        const precos = JSON.parse(response.retorno.response).filter(
          (x: { indCombustivel: string }) => x.indCombustivel !== 'R',
        );

        return precos
          .map((element: any) => {
            const pBomba = element.combustivel.precos.precoUnitario[0].preco;
            const pSps =
              element.combustivel.precos.precoNegociado[0].preco == 0
                ? pBomba
                : element.combustivel.precos.precoNegociado[0].preco;
            const pRedeEspecial =
              element.combustivel.precos.precoRedeEspecial[0].preco == 0
                ? pSps
                : element.combustivel.precos.precoRedeEspecial[0].preco;
            const descontoSps = this.getDescontoEmReais(pBomba, pSps);
            const descontoRedeEspecial = this.getDescontoEmReais(
              pBomba,
              pRedeEspecial,
            );

            return {
              ehRedeEspecial: element.redeEspecial || false,
              codCombustivel: element.indCombustivel || 0,
              descricaoCombustivel: element.descricaoCombustivel || '',
              precoBomba: pBomba || 0,
              precoSps: pSps || 0,
              precoRedeEspecial: Math.min(pRedeEspecial, pSps) || 0,
              descontoSps: descontoSps || 0,
              descontoRedeEspecial:
                Math.max(descontoRedeEspecial, descontoSps) || 0,
              dataUltimaAtualizacao:
                element.combustivel.datCadastro || new Date(),
              desatualizado: this.precoDesatualizado(
                element.combustivel.datCadastro,
              ),

              descricaoBomba:
                element.combustivel.precos.precoUnitario[0].descricao || '',
              descricaoSps:
                element.combustivel.precos.precoNegociado[0].descricao || '',
              descricaoRedeEspecial:
                element.combustivel.precos.precoRedeEspecial[0].descricao || '',
              precoAnterior: 0,
              dispovivelParaVenda:
                element.combustivel.disponivelParaVenda || false,
            };
          })
          .sort((a: any, b: any) => a.codCombustivel - b.codCombustivel);
      } else {
        throw new Error(response?.mensagens.join('\n'));
      }
    } catch (err: any) {
      console.error('Erro ao obter preços:', err.message);

      // throw new Error("tettevv");

      return [];
    }
  }

  // async manterPrecos(
  //   userGuid: string,
  //   codPosto?: number
  // ): Promise<IPostResponsePrecosList> {
  //   const url = `${environment.host_ctf_service}${CONFIG_URLS_API.API_ManterPrecos}?guid=${userGuid}`;

  //   var httpOptions = {
  //     headers: new HttpHeaders({
  //       Accept: 'application/json',
  //       'Content-Type': 'application/json',
  //       codPosto: codPosto ? codPosto.toString() : '',
  //     }),
  //   };

  //   try {
  //     var response = await firstValueFrom(
  //       this.http.get<IResponsePrecosList>(url, httpOptions)
  //     );
  //     if (response?.sucesso) {
  //       var ret = JSON.parse(response.retorno.response);
  //       return ret[0];
  //     }

  //     throw new Error('Falha ao obter preços');
  //   } catch (error: any) {
  //     return { COD_RETORNO: 99, DSC_RETORNO: 'Erro ao manter preços' };
  //   }
  // }

  private async enviarPrecoAtualizado(
    payload: string,
  ): Promise<IPostResponsePrecosList> {
    const url = `${environment.host_ctf_service}${CONFIG_URLS_API.API_CadastroDePreco}`;

    try {
      const response = await firstValueFrom(
        this.http.post<IResponsePrecosList>(url, payload, this.httpOptions),
      );

      if (response?.sucesso) {
        const ret = JSON.parse(response.retorno.response);
        const retorno = ret[0];

        return retorno;
      } else {
        throw new Error('Falha ao enviar preços');
      }
    } catch (error: any) {
      return { COD_RETORNO: 99, DSC_RETORNO: error.message };
    }
  }
  async enviarPrecosPosto(precos: string): Promise<IPostResponsePrecosList> {
    try {
      return await this.enviarPrecoAtualizado(precos);
    } catch (error) {
      return { COD_RETORNO: 99, DSC_RETORNO: 'Erro ao enviar preços' };
    }
  }

  verificarAcessoUploadCsv(guid: string): Observable<boolean> {
    const url = `${environment.host_ctf_service}${CONFIG_URLS_API.API_PostoAutorizadoUploadCsv}?guid=${guid}`;

    return this.http
      .get<any>(url, this.httpOptions)
      .pipe(map((response: any) => response?.retorno));
  }

  public getListaPrecosPosto(
    codPosto: number,
    guid: string,
  ): Observable<IPrecoPosto[]> {
    return from(
      this.getPrecos(codPosto, guid).then((precos) => {
        precos.map((preco) => {
          preco.possuiErros = false;
          preco.descricaoCombustivel = preco.descricaoCombustivel
            .toLowerCase()
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          preco.erros = [];
        });
        return precos;
      }),
    );
  }
}
